/**
 * Mana Locals Admin — HTTP transport.
 *
 * THE CONTRACT BOUNDARY. Every response that enters the application passes
 * through `request()` below and is parsed by its Zod schema. There is no path
 * that returns an unvalidated payload to a component.
 *
 * This is what makes self-owned schemas survivable. If the API's shape changes,
 * the result is a `ContractMismatchError` naming the endpoint, the request id and
 * the offending paths — one attributable failure — instead of `undefined`
 * rendering silently somewhere inside a screen.
 *
 * Zod validates SHAPE only. Nothing here is authorization.
 */
import { type z } from 'zod';
import { env } from '@/api/config/env';
import { emitAuthEvent } from '@/api/client/auth-events';
import { ApiError, ContractMismatchError, toApiProblem } from '@/api/client/problem';

const DEFAULT_TIMEOUT_MS = 15_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;

  /**
   * Required by the API for POST commands. Reused VERBATIM across a retry —
   * generating a fresh key on retry is how one user action becomes two.
   */
  idempotencyKey?: string;

  /** Entity `version` for If-Match optimistic concurrency. */
  ifMatch?: number;

  /**
   * The `If-Match` FORMAT this module's contract expects.
   *
   * The header is written two ways by the API, and the difference is not
   * cosmetic:
   *
   *   - `etag` — a quoted entity tag, `If-Match: "3"`. M13 (offers) and the
   *     other versioned resources take this form.
   *   - `bare-version` — a bare integer, `If-Match: 3`. M44 (content & media)
   *     takes this form, and refuses the quoted one with a `400`.
   *
   * The transport therefore does not GUESS: the format is named by the caller
   * that knows the contract. The default is the quoted form, which is what the
   * majority of the versioned resources use.
   */
  ifMatchFormat?: 'etag' | 'bare-version';

  query?: Record<string, string | number | boolean | undefined>;

  /**
   * Set on token-exchange endpoints so their 401s surface as-is instead of
   * triggering a silent refresh. Without this, a failed login can recurse.
   */
  skipAuthRefresh?: boolean;

  /** Per-request override; some operations legitimately exceed the default. */
  timeoutMs?: number;

  /**
   * The `X-Tenant-Id` SELECTOR header.
   *
   * Used by the PLATFORM verification surface, where the caller is a platform
   * verifier who is NOT a member of the tenant being reviewed. The tenant cannot
   * be resolved from a membership, so it is named explicitly. The server
   * re-checks the platform grant and the tenant's existence inside the database;
   * this header is a SELECTOR, never authorization.
   */
  tenantId?: string;
}

/**
 * A fresh key per user intent.
 *
 * The caller owns the key's lifetime: if a command may be retried, generate the
 * key once and pass the same value on every attempt.
 */
export function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

type Attempt = {
  ok: boolean;
  status: number;
  payload: unknown;
  requestId: string | undefined;
};

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), env.apiBaseUrl);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

function buildHeaders(options: RequestOptions, requestId: string): Headers {
  const headers = new Headers({
    Accept: 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    'X-Request-Id': requestId,
  });

  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey);
  if (options.ifMatch !== undefined) {
    const value =
      options.ifMatchFormat === 'bare-version'
        ? String(options.ifMatch)
        : `"${options.ifMatch}"`;
    headers.set('If-Match', value);
  }

  // A hint for the server to skip a redundant lookup. Never authorization.
  if (env.tenantSlug) headers.set('X-Tenant-Slug', env.tenantSlug);

  // The platform verification surface's tenant SELECTOR. Never authorization.
  if (options.tenantId) headers.set('X-Tenant-Id', options.tenantId);

  return headers;
}

async function performFetch(path: string, options: RequestOptions): Promise<Attempt> {
  const requestId = crypto.randomUUID();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  // Caller-supplied cancellation and the internal timeout must both abort.
  const onExternalAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort, { once: true });

  try {
    const response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers: buildHeaders(options, requestId),
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      signal: controller.signal,
      credentials: 'same-origin',
      cache: 'no-store',
    });


    const payload = await readBody(response);
    const serverRequestId = response.headers.get('X-Request-Id') ?? undefined;

    return {
      ok: response.ok,
      status: response.status,
      payload,
      requestId: serverRequestId ?? requestId,
    };
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * 204/205 carry no body; `res.json()` on them throws, which would turn a
 * legitimate empty success into a spurious failure.
 */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  const text = await response.text();
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Issues a request and returns a schema-validated body.
 *
 * On success the value is typed as the schema's output. On ANY failure — HTTP
 * error or contract drift — this throws. There is deliberately no path that
 * returns partially-valid data.
 */
export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const attempt = await performFetch(path, options);

  if (!attempt.ok) {
    // A 401 on an auth endpoint is the endpoint's own answer, not a session end.
    if (attempt.status === 401 && !options.skipAuthRefresh) {
      emitAuthEvent('unauthenticated');
    }
    throw toApiProblem(attempt.status, attempt.payload);
  }

  const parsed = schema.safeParse(attempt.payload);
  if (!parsed.success) {
    throw new ContractMismatchError({
      endpoint: path,
      status: attempt.status,
      requestId: attempt.requestId,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      })),
    });
  }

  return parsed.data;
}

export const http = {
  get: <T>(path: string, schema: z.ZodType<T>, options?: RequestOptions) =>
    request(path, schema, { ...options, method: 'GET' }),

  post: <T>(path: string, schema: z.ZodType<T>, options?: RequestOptions) =>
    request(path, schema, { ...options, method: 'POST' }),

  patch: <T>(path: string, schema: z.ZodType<T>, options?: RequestOptions) =>
    request(path, schema, { ...options, method: 'PATCH' }),

  delete: <T>(path: string, schema: z.ZodType<T>, options?: RequestOptions) =>
    request(path, schema, { ...options, method: 'DELETE' }),

  /**
   * Reports whether a tenant query is currently possible.
   *
   * The API exposes no health or discovery endpoint yet, so this reports what
   * the CLIENT knows, not what the server knows. Never present it to a user as
   * a server status.
   */
  canQueryTenants: (): boolean => env.tenantSlug !== undefined,
};

export { ApiError, ContractMismatchError };